<template>
  <div>
    <a-input-number
      v-if="type === 'number'"
      :value="modelValue"
      :placeholder="placeholder"
      :max="max"
      :min="min"
      style="width: 100%"
      :disabled="disabled"
      :addon-before="prefix"
      :addon-after="suffix"
      :formatter="formatValue"
      @step="updateStepValue"
      @input="updateAndFormatValue"
    />
    <a-input-number
      v-if="type === 'currency'"
      :value="modelValue"
      :placeholder="currencyPlaceholder"
      :max="max"
      :min="min"
      style="width: 100%"
      :disabled="disabled"
      :addon-before="prefix"
      :addon-after="currencySuffix"
      :formatter="formatCurrencyValue"
      @step="updateCurrencyStepValue"
      @input="updateAndFormatCurrencyValue"
    />
    <a-input-password
      v-if="type === 'password'"
      :placeholder="placeholder"
      :value="modelValue"
      :disabled="disabled"
      @input="updateValue"
    />
    <a-textarea
      v-if="type === 'textarea'"
      :value="modelValue"
      :rows="rows"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="updateValue"
    />
    <a-input
      v-if="type === ''"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :maxlength="maxTextInput"
      @input="updateValue"
    >
      <template #prefix>
        <slot name="prefix"></slot>
      </template>
      <template #suffix>
        <slot name="suffix"></slot>
      </template>
    </a-input>
  </div>
</template>

<script setup lang="ts">
import {
  useFormatNumber,
  useParseNumber,
  useFormatNumberByCurrency,
  useParseNumberByCurrency,
} from "@/composables/formatters";
import { CurrencyData } from "@/models/currencyData.interface";
import { computed, PropType } from "vue";

const props = defineProps({
  modelValue: { type: [String, Number], required: false, default: "" },
  placeholder: { type: String, required: false, default: "" },
  type: { type: String, required: false, default: "" },
  rows: { type: Number, required: false, default: 4 },
  max: { type: Number, required: false, default: Number.MAX_SAFE_INTEGER },
  min: { type: Number, required: false, default: 0 },
  disabled: { type: Boolean, required: false, default: false },
  prefix: { type: String, required: false, default: "" },
  suffix: { type: String, required: false, default: "" },
  currency: {
    type: Object as PropType<CurrencyData | undefined>,
    required: false,
    default: undefined,
  },
  integer: { type: Boolean, required: false, default: false },
});
const emit = defineEmits(["update:modelValue"]);

const updateValue = (event: InputEvent) => {
  emit("update:modelValue", (event.target as HTMLInputElement).value);
};

const updateStepValue = (event: InputEvent) => {
  emit("update:modelValue", event);
};

const formatValue = (n: number): string => {
  return useFormatNumber(n, props.integer);
};

const updateAndFormatValue = (event: InputEvent) => {
  emit(
    "update:modelValue",
    event ? useParseNumber(event.toString(), props.integer) : 0
  );
};

// Currency formatting functions
const currencySuffix = computed(() => props.currency?.symbol || "");

const currencyPlaceholder = computed(() => {
  // Show format hint like "0.00" or "0,00" based on currency
  // Format 0.01 to detect the decimal separator, then build "0.00" or "0,00"
  const formatted = useFormatNumberByCurrency(0.01, props.currency?.code);
  const decimalSeparator = formatted.includes(",") ? "," : ".";
  return `0${decimalSeparator}00`;
});

const formatCurrencyValue = (n: number): string => {
  return useFormatNumberByCurrency(n, props.currency?.code);
};

const updateCurrencyStepValue = (event: InputEvent) => {
  emit("update:modelValue", event);
};

const updateAndFormatCurrencyValue = (event: InputEvent) => {
  emit(
    "update:modelValue",
    event ? useParseNumberByCurrency(event.toString(), props.currency?.code) : 0
  );
};

const maxTextInput = computed(() => {
  return props.max !== Number.MAX_SAFE_INTEGER ? props.max : 255;
});
</script>

<style lang="scss" scoped>
.ant-input-affix-wrapper,
.ant-input-number {
  @apply shadow-sm  rounded-md;
}

.ant-input {
  @apply focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md shadow-sm;
}
</style>
